import { isResource } from '@medplum/core'
import type {
  CodeableConcept,
  Practitioner,
  Provenance,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeableConcept,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

export const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/provenance-participant-type'

export const ProvenanceParticipantType = {
  AUTHOR: 'author',
  SIGNER: 'signer',
} as const
export type ProvenanceParticipantType =
  (typeof ProvenanceParticipantType)[keyof typeof ProvenanceParticipantType]

export const PROVENANCE_PARTICIPANT_TYPE_MAP = {
  [ProvenanceParticipantType.AUTHOR]: codeableConcept({
    system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM,
    code: ProvenanceParticipantType.AUTHOR,
    display: 'Author',
  }),
  [ProvenanceParticipantType.SIGNER]: codeableConcept({
    system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM,
    code: ProvenanceParticipantType.SIGNER,
    display: 'Performer',
  }),
}

export class ExtendedProvenance extends ExtendedResource<Provenance> {
  static readonly RESOURCE_TYPE = 'Provenance'

  static isInstance(value: unknown): value is Provenance {
    return isResource(value) && isOfType(value, 'Provenance')
  }

  constructor({
    recorded,
    who,
    type,
    target,
    ...rest
  }: Omit<Provenance, 'resourceType' | 'recorded' | 'agent' | 'target'> & {
    who: Reference<Practitioner>
    target: Reference
    recorded?: string
    type: ProvenanceParticipantType
  }) {
    super({
      recorded: recorded ?? now(),
      agent: [
        {
          type: PROVENANCE_PARTICIPANT_TYPE_MAP[type],
          who: who,
        },
      ],
      target: [target],
      ...rest,
    })
  }

  get type(): ProvenanceParticipantType {
    const type = codeFromCodeableConcept(this.resource.agent?.[0]?.type)
    // TODO: remove this once we backfill
    return (
      (type as ProvenanceParticipantType) ?? ProvenanceParticipantType.AUTHOR
    )
  }

  get who(): Reference<Practitioner> {
    const who = this.resource.agent?.[0]?.who
    invariant(
      isOfType(who, 'Practitioner'),
      'Provenance must have a practitioner agent'
    )
    return who
  }

  get recorded(): string {
    return this.resource.recorded
  }

  get target(): Reference {
    return this.resource.target?.[0]
  }

  get activity(): CodeableConcept | undefined {
    return this.resource.activity
  }

  // `who` without the practitioner invariant — for callers probing whether a
  // human (vs a service client) performed the activity.
  get whoPractitioner(): Reference<Practitioner> | undefined {
    const who = this.resource.agent?.[0]?.who
    return isOfType(who, 'Practitioner') ? who : undefined
  }

  static readonly ACTIVITY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/provenance-activity`

  // Provenance has no `activity` search parameter in FHIR R4 — query by
  // target (most recent first) and filter on `activity` in code.
  static byTarget(target: Reference): QueryTypes {
    return {
      target: target.reference,
      _sort: '-recorded',
      _count: 1000,
    }
  }

  protected getExportFields() {
    return {
      id: this.id,
      recorded: this.recorded,
      type: this.type,
      target: this.target,
      who: this.who,
    }
  }
}

// Recorded when a staff member schedules a participant's disenrollment, so
// downstream automation (the disenrollment cleanup review task) can attribute
// the trigger to a human even though the disenrollment itself is later
// processed by the service-account cron.
export const provenanceActivityDisenrollmentScheduled = codeableConcept({
  system: ExtendedProvenance.ACTIVITY_SYSTEM,
  code: 'disenrollment-scheduled',
  display: 'Disenrollment Scheduled',
})

// Recorded when a staff member completes a queued-edits review (targeting the
// review Task), whether they approved or rejected the proposed changes — the
// rejection of every entry is itself a decision worth attributing. The
// per-entry approve/reject outcomes live on the Task's output.
export const provenanceActivityQueuedEditsReviewed = codeableConcept({
  system: ExtendedProvenance.ACTIVITY_SYSTEM,
  code: 'queued-edits-reviewed',
  display: 'Queued Edits Reviewed',
})
